// One step, as the technician follows it (T4): cautions first, the lead's
// note and photos, the parts with find number, quantity and unit (the part
// actually used, when substituted), the checks, and big Back, Done and
// Problem buttons.
import type { ReactNode } from 'react';

import type { BuildStepResponse } from '../../api/generated/BuildStepResponse';
import type { StepPartResponse } from '../../api/generated/StepPartResponse';
import type { UnitResponse } from '../../api/generated/UnitResponse';
import { Badge, Button } from '../../design/components';
import { formatQuantity } from '../../design/format';

function PartRow({ part, onDrawings }: { part: StepPartResponse; onDrawings: (itemId: string) => void }) {
    return (
        <tr>
            <td className="sf-mono">{part.findNumber ?? '—'}</td>
            <td>
                <strong className="sf-mono">{part.activePartNumber}</strong> {part.name ?? ''}
                {part.isSubstituted ? (
                    <div>
                        <Badge tone="pending">Substituted for {part.partNumber}</Badge>
                    </div>
                ) : null}
                {part.kind === 'SubAssembly' && part.builtInStep !== null ? (
                    <div className="sf-muted">Built in step {part.builtInStep}</div>
                ) : null}
                {part.kind === 'Arriving' ? (
                    <div className="sf-muted">Comes from {part.fromWorkCenter ?? 'another station'}</div>
                ) : null}
            </td>
            <td>{formatQuantity(part.quantity, part.unitOfMeasure)}</td>
            <td>
                <Button
                    aria-label={`Drawings for ${part.activePartNumber}`}
                    onClick={() => {
                        onDrawings(part.itemId);
                    }}
                >
                    Drawings
                </Button>
            </td>
        </tr>
    );
}

export function StepView({
    step,
    stepCount,
    unit,
    children,
    onDrawings,
}: {
    step: BuildStepResponse;
    stepCount: number;
    unit: UnitResponse | undefined;
    children?: ReactNode;
    onDrawings: (itemId: string) => void;
}) {
    const done = unit?.completions.find((completion) => completion.step === step.number);
    return (
        <article className="sf-card sf-stack" aria-labelledby={`step-${String(step.number)}`}>
            <div className="sf-row" style={{ justifyContent: 'space-between' }}>
                <h2 id={`step-${String(step.number)}`}>
                    Step {step.number} of {stepCount}: {step.title}
                </h2>
                {done ? <Badge tone="success">Done by {done.done.displayName}</Badge> : null}
            </div>
            {step.cautions.map((caution) => (
                <div key={caution} className="sf-note sf-note--pending" role="note">
                    Caution: {caution}
                </div>
            ))}
            {step.note ? <p>{step.note}</p> : null}
            {step.photos.length > 0 ? (
                <div className="sf-photo-strip">
                    {step.photos.map((photo, index) => (
                        <img
                            key={photo}
                            src={`/api/v1/instruction-photos/${photo}`}
                            alt={`How step ${String(step.number)} looks, view ${String(index + 1)}`}
                        />
                    ))}
                </div>
            ) : null}
            {step.assemblies.map((assembly) => (
                <div key={assembly.partNumber} className="sf-row">
                    <span>
                        Building <strong className="sf-mono">{assembly.activePartNumber}</strong> {assembly.name ?? ''}
                        {assembly.quantityPerUnit > 1 ? ` × ${String(assembly.quantityPerUnit)}` : ''}
                    </span>
                    <Button
                        onClick={() => {
                            onDrawings(assembly.itemId);
                        }}
                    >
                        Drawings for {assembly.activePartNumber}
                    </Button>
                </div>
            ))}
            {step.parts.length > 0 ? (
                <table className="sf-table">
                    <caption className="sf-visually-hidden">Parts for step {step.number}</caption>
                    <thead>
                        <tr>
                            <th scope="col">Find no.</th>
                            <th scope="col">Part</th>
                            <th scope="col">Quantity</th>
                            <th scope="col">
                                <span className="sf-visually-hidden">Drawings</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {step.parts.map((part) => (
                            <PartRow
                                key={`${part.partNumber}-${part.findNumber ?? ''}-${part.kind}`}
                                part={part}
                                onDrawings={onDrawings}
                            />
                        ))}
                    </tbody>
                </table>
            ) : null}
            {children}
        </article>
    );
}

export function StepActions({
    canBack,
    canDone,
    doneLabel,
    busy,
    onBack,
    onDone,
    onProblem,
}: {
    canBack: boolean;
    canDone: boolean;
    doneLabel: string;
    busy: boolean;
    onBack: () => void;
    onDone: () => void;
    onProblem: () => void;
}) {
    return (
        <div className="sf-step-actions sf-no-print">
            <Button large disabled={!canBack} onClick={onBack}>
                Back
            </Button>
            <Button large variant="primary" disabled={!canDone || busy} onClick={onDone}>
                {busy ? 'Saving…' : doneLabel}
            </Button>
            <Button large variant="danger" onClick={onProblem}>
                Problem
            </Button>
        </div>
    );
}
